import { useState, FormEvent } from 'react';
import RestaurantLogo from '@/components/branding/RestaurantLogo';
import { useBranding } from '@/hooks/useBranding';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { SITE } from '@/content/yawasCopy';

interface AdminLoginProps {
  onLogin: (password: string) => Promise<boolean>;
  isVerifying: boolean;
  error: string | null;
  onClearError: () => void;
}

const AdminLogin = ({ onLogin, isVerifying, error, onClearError }: AdminLoginProps) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { restaurantName } = useBranding();
  const { get } = useSiteSettings();
  const adminEmail = get('restaurant_email', SITE.email) || SITE.email;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password.trim() || isVerifying) return;
    await onLogin(password);
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Left decorative panel */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1a0e 50%, #1a1a1a 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-orange-500 blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 rounded-full bg-orange-400 blur-3xl"></div>
        </div>
        <div className="relative z-10 flex flex-col items-center gap-8 px-12 text-center">
          <RestaurantLogo maxHeight={200} invert />
          <div>
            <h2 className="text-white text-3xl font-bold mb-3">{restaurantName || 'Yönetici Paneli'}</h2>
            <p className="text-gray-400 text-base leading-relaxed">
              Menüyü, ana sayfa içeriğini ve restoran bilgilerini tek bir yerden yönetin.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 w-full max-w-xs mt-4">
            {[
              { icon: 'ri-restaurant-line', label: 'Menü' },
              { icon: 'ri-slideshow-3-line', label: 'Slider' },
              { icon: 'ri-layout-line', label: 'İçerik' },
              { icon: 'ri-settings-3-line', label: 'Ayarlar' },
            ].map((item) => (
              <div
                key={item.label}
                className="flex flex-col items-center gap-2 bg-white/5 rounded-xl p-4 border border-white/10"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${item.icon} text-orange-400 text-xl`}></i>
                </div>
                <span className="text-gray-300 text-xs font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <RestaurantLogo maxHeight={120} />
          </div>

          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <div className="mb-8">
              <h1 className="text-gray-900 text-2xl font-bold mb-1">Giriş Yap</h1>
              <p className="text-gray-500 text-sm">Yönetici hesabınızla giriş yapın</p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta
                </label>
                <input
                  type="email"
                  value={adminEmail}
                  readOnly
                  autoComplete="username"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 outline-none cursor-default"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Şifre
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); onClearError(); }}
                    placeholder="Şifrenizi girin"
                    className={`w-full px-4 py-3 pr-12 rounded-xl border text-sm text-gray-900 placeholder-gray-400 outline-none transition-all
                      ${error
                        ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                        : 'border-gray-200 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-50'
                      }`}
                    autoFocus
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
                {error && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-red-500 text-sm"></i>
                    </div>
                    <p className="text-red-500 text-xs font-medium">{error}</p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isVerifying || !password.trim()}
                className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
              >
                {isVerifying ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Doğrulanıyor...</span>
                  </>
                ) : (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-login-box-line"></i>
                    </div>
                    <span>Giriş Yap</span>
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Sadece yetkili personel erişebilir
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
